import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import PropertyCard from "@/components/PropertyCard";
import { ArrowLeft, Building2, MapPin, Calendar } from "lucide-react";
import { VerifiedBadge } from "@/components/VerifiedBadges";
import { FLAGS } from "@/lib/utils";

export default function DeveloperProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true); setErr("");
      try {
        const { data } = await api.get(`/developers/${id}`);
        if (!cancelled) setData(data);
      } catch (e) {
        if (!cancelled) setErr(e.response?.data?.detail || "Developer not found");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  const initials = data?.name?.split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase() || "?";

  return (
    <div className="max-w-7xl mx-auto" data-testid="developer-profile-page">
      <button
        data-testid="dev-profile-back"
        onClick={() => navigate(-1)}
        className="inline-flex items-center gap-2 text-sm text-neutral-600 hover:text-propera transition mb-6"
      >
        <ArrowLeft size={14} /> Back
      </button>

      {loading && <p className="text-neutral-500">Loading…</p>}
      {err && (
        <div className="soft-card p-8 text-center">
          <p className="font-semibold text-neutral-900">{err}</p>
          <Link to="/discover" className="mt-3 inline-block text-sm text-propera hover:underline">Back to Discover</Link>
        </div>
      )}

      {data && (
        <>
          {/* Header card */}
          <div className="soft-card p-6 sm:p-8">
            <div className="flex items-start gap-5 sm:gap-6 flex-wrap">
              <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-2xl bg-propera flex items-center justify-center flex-shrink-0 overflow-hidden">
                {data.avatar ? (
                  <img src={data.avatar} alt={data.name} className="w-full h-full object-cover" />
                ) : (
                  <span className="text-white text-2xl sm:text-3xl font-bold">{initials}</span>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[11px] uppercase tracking-[0.18em] text-propera font-semibold flex items-center gap-1.5">
                  <Building2 size={12} /> Property Developer
                </p>
                <h1 className="text-3xl sm:text-4xl font-bold text-neutral-900 mt-1.5">
                  {data.company_name || data.name}
                </h1>
                {data.verified && <div className="mt-2" data-testid="dev-verified-badge"><VerifiedBadge kind="developer" /></div>}
                {data.company_name && data.name && data.company_name !== data.name && (
                  <p className="text-sm text-neutral-500 mt-1">Led by {data.name}</p>
                )}
                <div className="flex flex-wrap gap-4 mt-4 text-sm text-neutral-600">
                  {data.country && (
                    <span className="flex items-center gap-1.5">
                      <MapPin size={13} /> {FLAGS[data.country] || ""} {data.country}
                    </span>
                  )}
                  {data.years_in_business && (
                    <span className="flex items-center gap-1.5">
                      <Calendar size={13} /> {data.years_in_business} years in business
                    </span>
                  )}
                  <span className="flex items-center gap-1.5">
                    <Building2 size={13} /> {data.developments_count} {data.developments_count === 1 ? "development" : "developments"} on Propera
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Developments */}
          <div className="mt-10">
            <h2 className="text-2xl font-bold text-neutral-900">Developments by this developer</h2>
            <p className="text-sm text-neutral-500 mt-1">All listings shown have passed Propera's manual verification.</p>

            {data.developments.length === 0 ? (
              <div className="soft-card p-10 mt-6 text-center">
                <p className="text-neutral-500">This developer doesn&apos;t have any live listings yet.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-6" data-testid="dev-profile-listings">
                {data.developments.map((d) => (
                  <PropertyCard key={d.id} dev={d} />
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
